import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../../lib/auth";
import { toast } from "sonner";
import {
  Inbox, MessageSquare, Send, Plus, Search, Mail, Instagram, Facebook, Phone,
  Star, StarOff, Archive, Pause, CheckCircle2, MoreVertical, RefreshCw, Settings as SettingsIcon,
  Trash2, Globe, Tag, X, Loader2, Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";

const CHANNEL_META = {
  whatsapp:  { label: "WhatsApp",  color: "bg-emerald-500", soft: "bg-emerald-50 text-emerald-700", icon: Phone },
  email:     { label: "Email",     color: "bg-sky-500",     soft: "bg-sky-50 text-sky-700",         icon: Mail },
  instagram: { label: "Instagram", color: "bg-fuchsia-500", soft: "bg-fuchsia-50 text-fuchsia-700", icon: Instagram },
  messenger: { label: "Messenger", color: "bg-blue-500",    soft: "bg-blue-50 text-blue-700",       icon: Facebook },
};

const STATUS_FILTERS = [
  { key: "open", lbl: "Open" },
  { key: "snoozed", lbl: "Snoozed" },
  { key: "closed", lbl: "Closed" },
  { key: "all", lbl: "All" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso); const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

// ─────────────────────── INBOX (3-COLUMN) ───────────────────────
export default function InboxPage() {
  const { subdomain } = useParams();
  const [conversations, setConversations] = useState([]);
  const [stats, setStats] = useState({});
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [statusFilter, setStatusFilter] = useState("open");
  const [channelFilter, setChannelFilter] = useState("all");
  const [searchQ, setSearchQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState("");
  const [isNote, setIsNote] = useState(false);
  const [planLocked, setPlanLocked] = useState(false);
  const threadEndRef = useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
      if (channelFilter && channelFilter !== "all") params.set("channel_type", channelFilter);
      if (searchQ.trim()) params.set("q", searchQ.trim());
      const [r1, r2, r3] = await Promise.all([
        api.get(`/t/inbox/conversations?${params.toString()}`),
        api.get("/t/inbox/stats"),
        api.get("/t/inbox/channels"),
      ]);
      setConversations(r1.data || []);
      setStats(r2.data || {});
      setChannels(r3.data || []);
      setPlanLocked(false);
    } catch (e) {
      if (e?.response?.status === 403) setPlanLocked(true);
      setConversations([]); setStats({}); setChannels([]);
    } finally { setLoading(false); }
  }, [statusFilter, channelFilter, searchQ]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Auto-pick first conversation when list loads
  useEffect(() => {
    if (!activeId && conversations[0]) setActiveId(conversations[0].id);
  }, [conversations, activeId]);

  const loadThread = async (cid) => {
    if (!cid) return;
    setLoadingThread(true);
    try {
      const r = await api.get(`/t/inbox/conversations/${cid}`);
      setThread(r.data.conversation);
      setMessages(r.data.messages || []);
    } catch { setThread(null); setMessages([]); }
    finally { setLoadingThread(false); }
  };
  useEffect(() => { loadThread(activeId); /* eslint-disable-next-line */ }, [activeId]);
  useEffect(() => { threadEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const sendMessage = async () => {
    const body = draft.trim();
    if (!body || !activeId) return;
    setSending(true);
    try {
      const r = await api.post(`/t/inbox/conversations/${activeId}/messages`, {
        body, direction: "out", is_note: isNote,
      });
      setMessages((prev) => [...prev, r.data]);
      setDraft("");
      // Update list preview without full reload
      setConversations((list) => list.map((c) => c.id === activeId ? { ...c, last_message: body.slice(0, 200), last_message_at: r.data.created_at } : c));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  const patchConv = async (patch, optimistic = true) => {
    if (!activeId) return;
    if (optimistic) {
      setThread((t) => t ? { ...t, ...patch } : t);
      setConversations((list) => list.map((c) => c.id === activeId ? { ...c, ...patch } : c));
    }
    try { await api.patch(`/t/inbox/conversations/${activeId}`, patch); toast.success("Updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); loadAll(); }
  };

  const filtered = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) =>
      (c.customer_name || "").toLowerCase().includes(q) ||
      (c.customer_handle || "").toLowerCase().includes(q) ||
      (c.last_message || "").toLowerCase().includes(q)
    );
  }, [conversations, searchQ]);

  if (planLocked) {
    return (
      <div className="p-8" data-testid="inbox-plan-locked">
        <div className="max-w-2xl mx-auto bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
          <Inbox size={32} className="mx-auto text-amber-700 mb-3" />
          <div className="font-display font-extrabold text-xl text-amber-900">Omnichannel Inbox is not in your plan</div>
          <p className="mt-2 text-sm text-amber-800">Upgrade your subscription to unify WhatsApp, Email, Instagram and Messenger conversations in one inbox.</p>
          <Link to={`/t/${subdomain}/billing`} className="inline-block mt-4 px-5 py-2.5 rounded-xl bg-[#1FA868] text-white font-bold text-sm">View plans</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-72px)] flex flex-col bg-[#0F1419]/[0.02]" data-testid="inbox-page">
      {/* HEADER */}
      <div className="px-6 py-4 bg-white border-b border-black/8 flex items-center gap-4">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#1B4332] to-[#1FA868] flex items-center justify-center text-white">
          <Inbox size={20} />
        </div>
        <div className="flex-1">
          <h1 className="font-display font-extrabold text-xl text-black">Omnichannel Inbox</h1>
          <div className="text-xs text-black/55">{stats.open_conversations || 0} open · {stats.unread_conversations || 0} unread · {channels.length} channel{channels.length === 1 ? "" : "s"}</div>
        </div>
        <button onClick={loadAll} className="p-2.5 rounded-xl hover:bg-black/5 text-black/70" title="Refresh" data-testid="inbox-refresh-btn"><RefreshCw size={18} /></button>
        <Link to={`/t/${subdomain}/inbox/channels`} className="px-3 py-2.5 rounded-xl bg-black/5 hover:bg-black/10 text-sm font-bold text-black/80 flex items-center gap-2" data-testid="inbox-channels-link"><SettingsIcon size={16} /> Channels</Link>
        <button onClick={() => setShowNew(true)} className="px-4 py-2.5 rounded-xl bg-[#1FA868] hover:bg-[#178a55] text-white text-sm font-bold flex items-center gap-2" data-testid="inbox-new-conv-btn"><Plus size={16} /> New</button>
      </div>

      {/* 3-COLUMN BODY */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT: Filters */}
        <aside className="w-56 border-r border-black/8 bg-white p-4 overflow-y-auto" data-testid="inbox-filter-rail">
          <div className="text-[10px] uppercase tracking-widest font-bold text-black/45 mb-2">Status</div>
          <div className="space-y-1">
            {STATUS_FILTERS.map((f) => (
              <button key={f.key} onClick={() => setStatusFilter(f.key)}
                data-testid={`inbox-filter-status-${f.key}`}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition ${statusFilter === f.key ? "bg-[#1FA868]/10 text-[#1FA868]" : "text-black/70 hover:bg-black/5"}`}>
                <span>{f.lbl}</span>
              </button>
            ))}
          </div>

          <div className="text-[10px] uppercase tracking-widest font-bold text-black/45 mt-5 mb-2">Channels</div>
          <div className="space-y-1">
            <button onClick={() => setChannelFilter("all")}
              data-testid="inbox-filter-channel-all"
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition ${channelFilter === "all" ? "bg-black/5 text-black font-bold" : "text-black/70 hover:bg-black/5"}`}>
              <Globe size={14} /> All channels
            </button>
            {Object.entries(CHANNEL_META).map(([k, m]) => {
              const Icon = m.icon;
              return (
                <button key={k} onClick={() => setChannelFilter(k)}
                  data-testid={`inbox-filter-channel-${k}`}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition ${channelFilter === k ? "bg-black/5 text-black font-bold" : "text-black/70 hover:bg-black/5"}`}>
                  <span className={`w-5 h-5 rounded-md ${m.color} text-white flex items-center justify-center`}><Icon size={11} /></span> {m.label}
                </button>
              );
            })}
          </div>

          <div className="text-[10px] uppercase tracking-widest font-bold text-black/45 mt-5 mb-2">Stats</div>
          <div className="space-y-2">
            <Stat label="Total" value={stats.total_conversations || 0} />
            <Stat label="Open" value={stats.open_conversations || 0} />
            <Stat label="Closed" value={stats.closed_conversations || 0} />
            <Stat label="Messages" value={stats.total_messages || 0} />
          </div>
        </aside>

        {/* MIDDLE: List */}
        <div className="w-[340px] border-r border-black/8 bg-white flex flex-col" data-testid="inbox-list-rail">
          <div className="p-3 border-b border-black/8">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40" />
              <input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadAll()}
                placeholder="Search conversations"
                className="w-full pl-8 pr-3 py-2 rounded-lg bg-black/5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1FA868]/30"
                data-testid="inbox-search-input"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-black/45 text-sm flex items-center justify-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>
            ) : filtered.length === 0 ? (
              <EmptyList onCreate={() => setShowNew(true)} hasChannels={channels.length > 0} subdomain={subdomain} />
            ) : (
              filtered.map((c) => (
                <ConvRow key={c.id} conv={c} active={c.id === activeId} onClick={() => setActiveId(c.id)} />
              ))
            )}
          </div>
        </div>

        {/* RIGHT: Thread */}
        <div className="flex-1 bg-[#FAFBF9] flex flex-col" data-testid="inbox-thread-rail">
          {!activeId ? (
            <div className="flex-1 flex items-center justify-center flex-col text-black/35">
              <MessageSquare size={56} strokeWidth={1.2} />
              <div className="mt-3 text-sm">Select a conversation to view</div>
            </div>
          ) : loadingThread ? (
            <div className="flex-1 flex items-center justify-center text-black/45 gap-2"><Loader2 className="animate-spin" size={16} /> Loading thread…</div>
          ) : !thread ? (
            <div className="flex-1 flex items-center justify-center text-black/45">Thread not found</div>
          ) : (
            <>
              {/* Thread header */}
              <div className="px-5 py-3 bg-white border-b border-black/8 flex items-center gap-3">
                <Avatar name={thread.customer_name} channel={thread.channel_type} />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-black truncate">{thread.customer_name}</div>
                  <div className="text-[11px] text-black/55 truncate">{thread.customer_handle} · {CHANNEL_META[thread.channel_type]?.label || thread.channel_type}</div>
                </div>
                <button onClick={() => patchConv({ starred: !thread.starred })} className="p-2 rounded-lg hover:bg-black/5" title={thread.starred ? "Unstar" : "Star"} data-testid="inbox-thread-star-btn">
                  {thread.starred ? <Star size={16} className="text-amber-500 fill-amber-500" /> : <StarOff size={16} className="text-black/55" />}
                </button>
                <button onClick={() => patchConv({ status: thread.status === "closed" ? "open" : "closed" })} className="px-3 py-1.5 rounded-lg bg-black/5 hover:bg-black/10 text-xs font-bold text-black/75 flex items-center gap-1.5" data-testid="inbox-thread-close-btn">
                  <CheckCircle2 size={13} /> {thread.status === "closed" ? "Reopen" : "Close"}
                </button>
                <button onClick={() => patchConv({ status: "snoozed" })} className="p-2 rounded-lg hover:bg-black/5" title="Snooze" data-testid="inbox-thread-snooze-btn">
                  <Pause size={15} className="text-black/55" />
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="inbox-thread-body">
                {messages.length === 0 && <div className="text-center text-black/40 text-sm">No messages yet. Send the first one below.</div>}
                {messages.map((m) => <Bubble key={m.id} msg={m} />)}
                <div ref={threadEndRef} />
              </div>

              {/* Composer */}
              <div className="p-3 bg-white border-t border-black/8" data-testid="inbox-composer">
                <div className="flex items-center gap-2 mb-2 text-xs">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={isNote} onChange={(e) => setIsNote(e.target.checked)} data-testid="inbox-composer-note-toggle" />
                    <span className={isNote ? "text-amber-700 font-bold" : "text-black/55"}>Internal note</span>
                  </label>
                  <span className="text-black/30 ml-auto">Enter to send · Shift+Enter for newline</span>
                </div>
                <div className={`flex items-end gap-2 rounded-xl border px-3 py-2 ${isNote ? "bg-amber-50 border-amber-200" : "bg-white border-black/15"}`}>
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
                    }}
                    rows={1}
                    placeholder={isNote ? "Write an internal note (only your team sees this)…" : "Type a reply…"}
                    className="flex-1 resize-none bg-transparent focus:outline-none text-sm py-1 max-h-32"
                    data-testid="inbox-composer-input"
                  />
                  <button onClick={sendMessage} disabled={!draft.trim() || sending} className="p-2 rounded-lg bg-[#1FA868] disabled:opacity-40 text-white" data-testid="inbox-composer-send-btn">
                    {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {showNew && <NewConversationModal channels={channels} onClose={() => setShowNew(false)} onCreated={(c) => { setShowNew(false); loadAll(); setActiveId(c.id); }} subdomain={subdomain} />}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-black/55">{label}</span>
      <span className="font-bold text-black">{value}</span>
    </div>
  );
}

function ConvRow({ conv, active, onClick }) {
  const meta = CHANNEL_META[conv.channel_type] || { color: "bg-black/30", label: conv.channel_type, icon: MessageSquare };
  return (
    <button onClick={onClick} data-testid={`inbox-conv-row-${conv.id}`}
      className={`w-full text-left px-4 py-3 border-b border-black/5 flex items-start gap-3 transition ${active ? "bg-[#1FA868]/8" : "hover:bg-black/[0.03]"}`}>
      <Avatar name={conv.customer_name} channel={conv.channel_type} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className={`font-bold text-sm truncate ${conv.unread_count ? "text-black" : "text-black/85"}`}>{conv.customer_name || conv.customer_handle}</div>
          <div className="text-[10px] text-black/45 ml-auto flex-shrink-0">{timeAgo(conv.last_message_at)}</div>
        </div>
        <div className="text-xs text-black/55 truncate mt-0.5">{conv.last_message || <span className="italic text-black/35">No messages yet</span>}</div>
        <div className="flex items-center gap-1.5 mt-1.5">
          <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${meta.soft || "bg-black/5 text-black/60"}`}>{meta.label}</span>
          {conv.status === "snoozed" && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-violet-50 text-violet-700">Snoozed</span>}
          {conv.status === "closed" && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-black/10 text-black/65">Closed</span>}
          {conv.starred && <Star size={10} className="text-amber-500 fill-amber-500" />}
          {!!conv.unread_count && <span className="ml-auto px-1.5 min-w-[18px] h-[18px] rounded-full bg-[#1FA868] text-white text-[10px] font-bold flex items-center justify-center">{conv.unread_count}</span>}
        </div>
      </div>
    </button>
  );
}

function Avatar({ name, channel }) {
  const meta = CHANNEL_META[channel] || { color: "bg-black/40", icon: MessageSquare };
  const initials = (name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  const Icon = meta.icon;
  return (
    <div className="relative flex-shrink-0">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-black/65 to-black/85 text-white flex items-center justify-center text-xs font-bold">{initials}</div>
      <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ${meta.color} text-white flex items-center justify-center ring-2 ring-white`}><Icon size={9} /></div>
    </div>
  );
}

function Bubble({ msg }) {
  if (msg.is_note) {
    return (
      <div className="flex justify-center" data-testid={`inbox-msg-${msg.id}`}>
        <div className="max-w-md bg-amber-50 border border-amber-200 rounded-2xl px-4 py-2.5 text-xs text-amber-900">
          <div className="flex items-center gap-1.5 mb-1 font-bold"><Sparkles size={11} /> Internal note · {msg.sender_name}</div>
          <div>{msg.body}</div>
          <div className="mt-1 text-[10px] text-amber-700/70">{timeAgo(msg.created_at)} ago</div>
        </div>
      </div>
    );
  }
  const outgoing = msg.direction === "out";
  return (
    <div className={`flex ${outgoing ? "justify-end" : "justify-start"}`} data-testid={`inbox-msg-${msg.id}`}>
      <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 text-sm ${outgoing ? "bg-[#1FA868] text-white rounded-br-md" : "bg-white border border-black/8 rounded-bl-md"}`}>
        <div className="whitespace-pre-wrap break-words">{msg.body}</div>
        <div className={`mt-1 text-[10px] ${outgoing ? "text-white/70" : "text-black/45"}`}>
          {outgoing ? msg.sender_name : ""}{outgoing ? " · " : ""}{timeAgo(msg.created_at)} ago{outgoing && msg.status ? ` · ${msg.status}` : ""}
        </div>
      </div>
    </div>
  );
}

function EmptyList({ onCreate, hasChannels, subdomain }) {
  return (
    <div className="p-6 text-center" data-testid="inbox-empty-state">
      <div className="w-14 h-14 rounded-2xl bg-[#1FA868]/10 text-[#1FA868] mx-auto flex items-center justify-center"><MessageSquare size={22} /></div>
      <div className="mt-3 font-bold text-sm text-black">No conversations yet</div>
      <p className="text-xs text-black/55 mt-1 leading-relaxed">
        {hasChannels ? "Start one manually or wait for inbound messages." : "Connect a channel first so customers can reach you."}
      </p>
      {hasChannels ? (
        <button onClick={onCreate} className="mt-4 px-4 py-2 rounded-lg bg-[#1FA868] text-white text-xs font-bold">+ New conversation</button>
      ) : (
        <Link to={`/t/${subdomain}/inbox/channels`} className="mt-4 inline-block px-4 py-2 rounded-lg bg-[#1FA868] text-white text-xs font-bold">Connect a channel</Link>
      )}
    </div>
  );
}

function NewConversationModal({ channels, onClose, onCreated, subdomain }) {
  const [channel_id, setChannelId] = useState(channels[0]?.id || "");
  const [customer_name, setName] = useState("");
  const [customer_handle, setHandle] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!channel_id) return toast.error("Pick a channel");
    if (!customer_handle.trim()) return toast.error("Customer handle required");
    setSaving(true);
    try {
      const r = await api.post("/t/inbox/conversations", { channel_id, customer_name, customer_handle });
      toast.success("Conversation created");
      onCreated(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="inbox-new-conv-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-extrabold text-lg text-black">New conversation</div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5"><X size={16} /></button>
        </div>
        {channels.length === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
            You don't have any channels connected. <Link to={`/t/${subdomain}/inbox/channels`} className="font-bold underline" onClick={onClose}>Connect one first.</Link>
          </div>
        ) : (
          <div className="space-y-3">
            <Field label="Channel">
              <select value={channel_id} onChange={(e) => setChannelId(e.target.value)} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="inbox-new-conv-channel">
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>{CHANNEL_META[c.type]?.label || c.type} — {c.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Customer name (optional)">
              <input value={customer_name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="inbox-new-conv-name" />
            </Field>
            <Field label="Handle (phone / email / IG @)">
              <input value={customer_handle} onChange={(e) => setHandle(e.target.value)} placeholder="+2348011112222" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="inbox-new-conv-handle" />
            </Field>
            <div className="flex gap-2 pt-2">
              <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg bg-black/5 text-sm font-bold text-black/70">Cancel</button>
              <button onClick={submit} disabled={saving} className="flex-1 px-4 py-2.5 rounded-lg bg-[#1FA868] text-white text-sm font-bold disabled:opacity-50" data-testid="inbox-new-conv-submit">
                {saving ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wide font-bold text-black/55">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ─────────────────────── CHANNELS SETTINGS ───────────────────────
export function InboxChannelsPage() {
  const { subdomain } = useParams();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // channel obj OR { _new: true, type: 'whatsapp' }
  const [planLocked, setPlanLocked] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/t/inbox/channels");
      setChannels(r.data || []);
      setPlanLocked(false);
    } catch (e) {
      if (e?.response?.status === 403) setPlanLocked(true);
      setChannels([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (cid) => {
    if (!window.confirm("Delete this channel? Conversations remain.")) return;
    try { await api.delete(`/t/inbox/channels/${cid}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (planLocked) {
    return (
      <div className="p-8" data-testid="inbox-channels-plan-locked">
        <div className="max-w-2xl mx-auto bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
          <Inbox size={28} className="mx-auto text-amber-700 mb-2" />
          <div className="font-display font-extrabold text-lg text-amber-900">Omnichannel Inbox is not in your plan</div>
          <Link to={`/t/${subdomain}/billing`} className="inline-block mt-3 px-5 py-2 rounded-xl bg-[#1FA868] text-white font-bold text-sm">View plans</Link>
        </div>
      </div>
    );
  }

  const present = new Set(channels.map((c) => c.type));

  return (
    <div className="p-6 lg:p-8 max-w-5xl" data-testid="inbox-channels-page">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-black">Inbox Channels</h1>
          <p className="text-sm text-black/60 mt-1">Connect WhatsApp, Email, Instagram and Messenger to receive customer messages in one unified inbox.</p>
        </div>
        <Link to={`/t/${subdomain}/inbox`} className="px-4 py-2 rounded-xl bg-black/5 hover:bg-black/10 text-sm font-bold text-black/75">← Back to inbox</Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {Object.entries(CHANNEL_META).map(([type, meta]) => {
          const existing = channels.find((c) => c.type === type);
          const Icon = meta.icon;
          return (
            <div key={type} className="bg-white rounded-2xl border border-black/8 p-5" data-testid={`inbox-channel-card-${type}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-11 h-11 rounded-xl ${meta.color} text-white flex items-center justify-center`}><Icon size={20} /></div>
                <div className="flex-1">
                  <div className="font-display font-extrabold text-lg text-black">{meta.label}</div>
                  <div className="text-xs text-black/55">{existing ? (existing.status === "connected" ? "✓ Connected" : "Configured (incomplete)") : "Not connected"}</div>
                </div>
              </div>
              {existing && (
                <div className="text-xs text-black/65 space-y-1 mb-3">
                  <div className="truncate"><span className="text-black/45">Name:</span> {existing.name}</div>
                  {existing.business_phone && <div className="truncate"><span className="text-black/45">Phone:</span> {existing.business_phone}</div>}
                  {existing.phone_number_id && <div className="truncate"><span className="text-black/45">Phone ID:</span> {existing.phone_number_id}</div>}
                  {existing.access_token_masked && <div className="truncate"><span className="text-black/45">Token:</span> <code className="bg-black/5 px-1 rounded">{existing.access_token_masked}</code></div>}
                  {existing.smtp_host && <div className="truncate"><span className="text-black/45">SMTP:</span> {existing.smtp_user}@{existing.smtp_host}:{existing.smtp_port}</div>}
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={() => setEditing(existing || { _new: true, type })} className="flex-1 px-3 py-2 rounded-lg bg-[#1FA868] text-white text-xs font-bold" data-testid={`inbox-channel-${type}-edit`}>
                  {existing ? "Edit / Reconnect" : "Connect"}
                </button>
                {existing && <button onClick={() => remove(existing.id)} className="px-3 py-2 rounded-lg bg-red-50 text-red-700 text-xs font-bold" data-testid={`inbox-channel-${type}-delete`}><Trash2 size={13} /></button>}
              </div>
            </div>
          );
        })}
      </div>

      {!loading && channels.length > 0 && (
        <div className="bg-sky-50 border border-sky-200 rounded-2xl p-5 text-sm text-sky-900">
          <div className="font-bold mb-1">📥 WhatsApp Webhook URL</div>
          <p className="text-xs text-sky-800 mb-2">Paste this into your Meta App → WhatsApp → Configuration → Webhook URL:</p>
          <code className="block bg-white border border-sky-200 rounded-lg px-3 py-2 text-xs font-mono text-sky-900 break-all" data-testid="inbox-webhook-url">
            {window.location.origin}/api/inbox/webhook/whatsapp
          </code>
          <p className="text-[11px] text-sky-700 mt-2">Use the <strong>Verify Token</strong> you set on the channel when subscribing.</p>
        </div>
      )}

      {editing && <ChannelEditor channel={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ChannelEditor({ channel, onClose, onSaved }) {
  const isNew = !!channel._new;
  const type = channel.type;
  const meta = CHANNEL_META[type];
  const [form, setForm] = useState({
    name: channel.name || meta.label,
    enabled: channel.enabled !== false,
    phone_number_id: channel.phone_number_id || "",
    access_token: "",
    webhook_verify_token: channel.webhook_verify_token || "",
    business_phone: channel.business_phone || "",
    smtp_host: channel.smtp_host || "",
    smtp_port: channel.smtp_port || 587,
    smtp_user: channel.smtp_user || "",
    smtp_password: "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { type, ...form };
      if (isNew) await api.post("/t/inbox/channels", payload);
      else await api.patch(`/t/inbox/channels/${channel.id}`, payload);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid={`inbox-channel-editor-${type}`}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-black/8 flex items-center justify-between sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg ${meta.color} text-white flex items-center justify-center`}><meta.icon size={15} /></div>
            <div className="font-display font-extrabold text-base text-black">{isNew ? "Connect" : "Edit"} {meta.label}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Display name"><input value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid={`inbox-editor-${type}-name`} /></Field>
          {type === "whatsapp" && (
            <>
              <Field label="Business phone (E.164)"><input value={form.business_phone} onChange={(e) => set("business_phone", e.target.value)} placeholder="+2348012345678" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="inbox-editor-whatsapp-phone" /></Field>
              <Field label="Phone Number ID (from Meta)"><input value={form.phone_number_id} onChange={(e) => set("phone_number_id", e.target.value)} placeholder="123456789012345" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="inbox-editor-whatsapp-pnid" /></Field>
              <Field label="Permanent Access Token"><input value={form.access_token} onChange={(e) => set("access_token", e.target.value)} type="password" placeholder={isNew ? "EAAxxxxxxxxxxxxx" : "Leave blank to keep existing"} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm font-mono" data-testid="inbox-editor-whatsapp-token" /></Field>
              <Field label="Webhook Verify Token (you create this)"><input value={form.webhook_verify_token} onChange={(e) => set("webhook_verify_token", e.target.value)} placeholder="myrandomtoken123" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="inbox-editor-whatsapp-verify" /></Field>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900">
                <strong>How to connect:</strong> Get your Phone Number ID and Permanent Access Token from{" "}
                <a href="https://developers.facebook.com" target="_blank" rel="noreferrer" className="underline font-bold">Meta for Developers</a>{" "}→ Your App → WhatsApp → API Setup.
              </div>
            </>
          )}
          {type === "email" && (
            <>
              <Field label="SMTP Host"><input value={form.smtp_host} onChange={(e) => set("smtp_host", e.target.value)} placeholder="smtp.gmail.com" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="SMTP Port"><input type="number" value={form.smtp_port} onChange={(e) => set("smtp_port", parseInt(e.target.value, 10) || 587)} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="SMTP User (email)"><input value={form.smtp_user} onChange={(e) => set("smtp_user", e.target.value)} placeholder="you@company.com" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="SMTP Password / App Password"><input value={form.smtp_password} onChange={(e) => set("smtp_password", e.target.value)} type="password" placeholder={isNew ? "••••••" : "Leave blank to keep existing"} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-900">Full IMAP polling lands in Phase 3 — outbound replies via SMTP work in Phase 2.</div>
            </>
          )}
          {(type === "instagram" || type === "messenger") && (
            <>
              <Field label="Page Access Token"><input value={form.access_token} onChange={(e) => set("access_token", e.target.value)} type="password" placeholder={isNew ? "Meta page access token" : "Leave blank to keep existing"} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="Webhook Verify Token"><input value={form.webhook_verify_token} onChange={(e) => set("webhook_verify_token", e.target.value)} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">Coming in Phase 4 — credentials stored now, wire-up activated next.</div>
            </>
          )}
          <div className="flex items-center gap-2 pt-2">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg bg-black/5 text-sm font-bold text-black/70">Cancel</button>
            <button onClick={submit} disabled={saving} className="flex-1 px-4 py-2.5 rounded-lg bg-[#1FA868] text-white text-sm font-bold disabled:opacity-50" data-testid={`inbox-editor-${type}-save`}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
